<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { EventKind, EventFrequency, PENSION_TYPE_LABELS, type ScenarioEvent, type ScenarioEventInit, type PensionType } from '@fire-calc/domain-ts';
import InputCardHeader from './InputCardHeader.vue';
import { EventDialog, ConfirmDialog, EmptyState, StatusPill, formatCzk, type EventFormData } from '@fire-calc/ui-vue';

interface StoreLike {
  addEvent: (data: Omit<ScenarioEventInit, 'id'> & { id: string }) => void;
  updateEvent: (id: string, data: Partial<ScenarioEventInit>) => void;
  removeEvent: (id: string) => void;
  duplicateEvent: (id: string) => void;
}

const props = defineProps<{
  events: readonly ScenarioEvent[];
  currentAge: number;
  currentAgeYear: number;
  store: StoreLike;
}>();

const emit = defineEmits<{
  addIncome: [];
  addExpense: [];
  editEvent: [event: ScenarioEvent];
  deleteEvent: [id: string];
  duplicateEvent: [id: string];
  toggleEvent: [id: string];
}>();

const { t } = useI18n();

// Dialog state
const showEventDialog = ref(false);
const editingEvent = ref<ScenarioEvent | null>(null);
const initialKind = ref<EventKind>(EventKind.extraExpense);
const pendingDeleteId = ref<string | null>(null);

const incomeEvents = computed(() =>
  props.events.filter((e) => e.kind === EventKind.income),
);

const expenseEvents = computed(() =>
  props.events.filter((e) => e.kind === EventKind.extraExpense),
);

// Pure conversion: domain entity → plain object for EventDialog
function eventToDialogFormat(e: ScenarioEvent): {
  id: string; kind: EventKind; label: string; amount: number; startAge: number;
  frequency: EventFrequency; durationYears: number; inflationAdjusted: boolean;
  enabled: boolean; note?: string; pensionType?: PensionType; growthRate?: number;
} {
  const obj: {
    id: string; kind: EventKind; label: string; amount: number; startAge: number;
    frequency: EventFrequency; durationYears: number; inflationAdjusted: boolean;
    enabled: boolean; note?: string; pensionType?: PensionType; growthRate?: number;
  } = {
    id: e.id,
    kind: e.kind,
    label: e.label,
    amount: e.amount.value,
    startAge: e.startAge.value,
    frequency: e.frequency,
    durationYears: e.durationYears,
    inflationAdjusted: e.inflationAdjusted,
    enabled: e.enabled,
  };
  if (e.note != null) obj.note = e.note;
  if (e.pensionType != null) obj.pensionType = e.pensionType;
  if (e.growthRate != null) obj.growthRate = e.growthRate;
  return obj;
}

const existingEventsForDialog = computed(() =>
  props.events.map(eventToDialogFormat),
);

const editingEventForDialog = computed(() =>
  editingEvent.value ? eventToDialogFormat(editingEvent.value) : undefined,
);

function getYearHint(event: ScenarioEvent): number {
  return props.currentAgeYear + (event.startAge.value - props.currentAge);
}

function getFormattedAmount(event: ScenarioEvent): string {
  return formatCzk(event.amount);
}

function getEventDisplayLabel(event: ScenarioEvent): string {
  return event.label || getFormattedAmount(event);
}

function handleAddIncome(): void {
  editingEvent.value = null;
  initialKind.value = EventKind.income;
  showEventDialog.value = true;
  emit('addIncome');
}

function handleAddExpense(): void {
  editingEvent.value = null;
  initialKind.value = EventKind.extraExpense;
  showEventDialog.value = true;
  emit('addExpense');
}

function openEditDialog(event: ScenarioEvent): void {
  editingEvent.value = event;
  showEventDialog.value = true;
  emit('editEvent', event);
}

function openDeleteConfirm(id: string): void {
  pendingDeleteId.value = id;
}

function handleDialogCreate(data: EventFormData): void {
  const eventId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `evt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  props.store.addEvent({
    id: eventId,
    kind: data.kind,
    label: data.label,
    amount: data.amount,
    startAge: data.startAge,
    frequency: data.frequency,
    durationYears: data.period === 'years' ? data.durationYears : data.period === 'once' ? 1 : 0,
    inflationAdjusted: data.inflationAdjusted,
    enabled: true,
    ...(data.pensionType ? { pensionType: data.pensionType } : {}),
    ...(data.growthRate != null ? { growthRate: data.growthRate } : {}),
  });
  showEventDialog.value = false;
  editingEvent.value = null;
}

function handleDialogUpdate(data: EventFormData): void {
  if (!editingEvent.value) return;
  props.store.updateEvent(editingEvent.value.id, {
    kind: data.kind,
    label: data.label,
    amount: data.amount,
    startAge: data.startAge,
    frequency: data.frequency,
    durationYears: data.period === 'years' ? data.durationYears : data.period === 'once' ? 1 : 0,
    inflationAdjusted: data.inflationAdjusted,
    ...(data.pensionType !== undefined ? { pensionType: data.pensionType } : {}),
    ...(data.growthRate !== undefined ? { growthRate: data.growthRate } : {}),
  });
  showEventDialog.value = false;
  editingEvent.value = null;
}

function handleDialogSaveAsNew(data: EventFormData): void {
  const eventId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `evt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  props.store.addEvent({
    id: eventId,
    kind: data.kind,
    label: data.label,
    amount: data.amount,
    startAge: data.startAge,
    frequency: data.frequency,
    durationYears: data.period === 'years' ? data.durationYears : data.period === 'once' ? 1 : 0,
    inflationAdjusted: data.inflationAdjusted,
    enabled: true,
    ...(data.pensionType ? { pensionType: data.pensionType } : {}),
    ...(data.growthRate != null ? { growthRate: data.growthRate } : {}),
  });
  showEventDialog.value = false;
  editingEvent.value = null;
}

function handleCloseDialog(): void {
  showEventDialog.value = false;
  editingEvent.value = null;
}

function handleDeleteConfirm(): void {
  if (pendingDeleteId.value) {
    props.store.removeEvent(pendingDeleteId.value);
    pendingDeleteId.value = null;
  }
}

function handleToggleEnabled(id: string): void {
  const event = props.events.find((e) => e.id === id);
  if (event) {
    props.store.updateEvent(id, { enabled: !event.enabled });
  }
  emit('toggleEvent', id);
}

function handleDuplicate(id: string): void {
  props.store.duplicateEvent(id);
  emit('duplicateEvent', id);
}

function handleDeleteRequest(id: string): void {
  emit('deleteEvent', id);
  openDeleteConfirm(id);
}

function getText(key: string, params?: Record<string, unknown>): string {
  try {
    return t(key, params as Record<string, string | number>);
  } catch {
    return key;
  }
}

function isCurrentAgeShifted(event: ScenarioEvent): boolean {
  return event.startAge.value <= props.currentAge;
}

interface GroupedSection {
  headingKey: string;
  emptyKey: string;
  testId: string;
  eventList: readonly ScenarioEvent[];
}

const groupedSections = computed<GroupedSection[]>(() => [
  {
    headingKey: 'eventColumnExpenses',
    emptyKey: 'eventEmptyExpenses',
    testId: 'expenses-column',
    eventList: expenseEvents.value,
  },
  {
    headingKey: 'eventColumnIncomes',
    emptyKey: 'eventEmptyIncomes',
    testId: 'incomes-column',
    eventList: incomeEvents.value,
  },
]);
</script>

<template>
  <article data-testid="card-events" class="bg-bg-card rounded-extra-large border border-border-light p-lg">
    <InputCardHeader :title="t('cardEventsTitle')" icon="menu">
      <template #leading>
        <span class="w-8 h-8 rounded-pill bg-brand-100 text-label-small font-bold text-brand-700 flex items-center justify-center">3</span>
      </template>
    </InputCardHeader>

    <!-- Add buttons row -->
    <div class="flex justify-between items-center mb-md">
      <button
        type="button"
        data-testid="add-income-btn"
        class="px-md py-sm rounded-pill bg-bg-pill text-text-primary text-label-medium font-medium hover:opacity-90 transition-opacity"
        @click="handleAddIncome"
      >
        {{ t('eventAddIncome') }}
      </button>
      <button
        type="button"
        data-testid="add-expense-btn"
        class="px-md py-sm rounded-pill bg-bg-pill text-text-primary text-label-medium font-medium hover:opacity-90 transition-opacity"
        @click="handleAddExpense"
      >
        {{ t('eventAddExpense') }}
      </button>
    </div>

    <!-- Two-column layout: desktop grid, mobile stack -->
    <div class="lg:grid lg:grid-cols-2 lg:gap-lg">
      <template v-for="(section, idx) in groupedSections" :key="section.testId">
        <section :aria-label="idx === 0 ? 'expenses' : 'incomes'">
          <h3
            :data-testid="section.testId"
            class="text-title-small font-semibold text-text-primary mb-sm"
          >
            {{ t(section.headingKey) }}
          </h3>

          <template v-if="section.eventList.length > 0">
            <div
              v-for="event in section.eventList"
              :key="event.id"
              :data-testid="`event-item-${event.id}`"
              class="flex items-center gap-sm py-sm border-b border-border-light last:border-b-0"
              :class="{ 'opacity-50': !event.enabled }"
            >
              <!-- Event label / amount -->
              <span class="text-body-medium font-medium text-text-primary flex-1 min-w-0 truncate">
                {{ getEventDisplayLabel(event) }}
              </span>

              <!-- Start age + year hint -->
              <span class="text-body-small text-text-secondary">
                {{ event.startAge.value }} · {{ getYearHint(event) }}
              </span>

              <!-- Current-age change badge (event already started or overdue) -->
              <StatusPill
                v-if="isCurrentAgeShifted(event)"
                data-testid="event-current-age-badge"
                type="warning"
                :label="t('eventBadgeCurrentAge')"
                :icon="false"
              />

              <!-- Pension badge (if applicable) -->
              <StatusPill
                v-if="event.pensionType && event.pensionType !== 'none'"
                type="info"
                :label="PENSION_TYPE_LABELS[event.pensionType]"
                :icon="false"
              />

              <!-- Action icons -->
              <div class="flex items-center gap-xs ml-sm">
                <button
                  type="button"
                  :data-testid="`event-toggle-${event.id}`"
                  class="w-5 h-5 text-text-tertiary hover:text-text-primary transition-colors duration-fast"
                  @click="handleToggleEnabled(event.id)"
                >
                  <svg v-if="event.enabled" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                </button>
                <button
                  type="button"
                  :data-testid="`event-edit-${event.id}`"
                  class="w-5 h-5 text-text-tertiary hover:text-text-primary transition-colors duration-fast"
                  @click="openEditDialog(event)"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button
                  type="button"
                  :data-testid="`event-delete-${event.id}`"
                  class="w-5 h-5 text-text-tertiary hover:text-error transition-colors duration-fast"
                  @click="handleDeleteRequest(event.id)"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
                <button
                  type="button"
                  :data-testid="`event-duplicate-${event.id}`"
                  class="w-5 h-5 text-text-tertiary hover:text-text-primary transition-colors duration-fast"
                  @click="handleDuplicate(event.id)"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </div>
            </div>
          </template>
          <EmptyState v-else :title="t(section.emptyKey)" />
        </section>

        <!-- Mobile divider between expense and income sections -->
        <div v-if="idx === 0" class="lg:hidden border-t border-border-light mt-md pt-md" />
      </template>
    </div>

    <!-- EventDialog -->
    <EventDialog
      v-if="showEventDialog"
      :event="editingEventForDialog!"
      :current-age="currentAge"
      :get-text="getText"
      :existing-events="existingEventsForDialog"
      :initial-kind="initialKind"
      @create="handleDialogCreate"
      @update="handleDialogUpdate"
      @save-as-new="handleDialogSaveAsNew"
      @close="handleCloseDialog"
    />

    <!-- ConfirmDialog for delete -->
    <ConfirmDialog
      v-if="pendingDeleteId"
      :title="t('confirmDialogTitle')"
      :message="t('confirmDeleteMessage')"
      :get-text="(key) => t(key)"
      @confirm="handleDeleteConfirm"
      @close="pendingDeleteId = null"
    />
  </article>
</template>
